import './contentgrid.css'
import { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { isNullOrEmpty } from '../helpers/ObjectHelpers';
import Icons, { ShipDuotoneIcon } from './icons/Icons';
import { CopyToClipboard } from './CopyToClipboard';
import { IconColors } from '../../services/SystemNames';
import {
    Lighthouse,
    Clock,
    Info,
    HandWaving,
    HourglassMedium,
    Pulse,
    ClockCounterClockwise,
    HandCoins,
    BookOpenText,
    UserCircle,
    Gear,
    DeviceMobile,
    MapPin,
    Alarm,
    Student,
    Certificate,
    Stack,
    NotePencil,
    Calendar,
    SlidersHorizontal,
} from '@phosphor-icons/react/dist/ssr';
import { SvgIcon } from '@progress/kendo-react-common';
import { SvgStyle } from './SvgStyle';
import { CalendarStar, QuestionMark } from '@phosphor-icons/react';
import { AddressBook, Anchor, Archive, ArrowFatLinesRight, BellSimpleRinging, Boat, CheckFat, FileText, GraduationCap, Lifebuoy, Medal, PaperPlaneTilt, Star, Swap, Toolbox } from 'phosphor-react-sc';
import PullToRefresh from './pull-to-refresh/PullToRefresh';

export default function ContentGrid(
    {
        children,
        id = "",
        title = "",
        titleIcon = "",
        subTitle = "",
        defaultContentClassName = "",
        defaultContentTitleClassName = "",
        className = "",
        onPullToRefresh = null,
        canCopySubTitle = false,
        autoHideTitle = true,
        panel = null,
        addContentSpace = true,
        fullscreen = false,
        showOfflineWarning = true
    }
) {

    const [warning, setWarning] = useState("");

    useEffect(() => {
        window.addEventListener("offline", handleOnOffline);
        window.addEventListener("online", handleOnOffline);

        handleOnOffline();

        return function cleanupEventListeners() {
            window.removeEventListener("offline", handleOnOffline);
            window.removeEventListener("online", handleOnOffline);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PullToRefresh
            onRefresh={onPullToRefresh}
            isPullable={onPullToRefresh !== null}
            pullingContent={<div className="ptr-content">&darr;  Dra ned for &aring; laste inn p&aring; ny &darr;</div>}
        >
            {
            fullscreen ?
                <div style={{ height: "100%"} }>{children}</div>
                :
                        
                <div className={`content-grid ${className}`} id={id}>
                    {
                        (!isNullOrEmpty(warning) && showOfflineWarning) &&
                            <div className="content-warning">{parse(warning)}</div>
                    }
                    <ContentGridTitle
                        title={title}
                        subTitle={subTitle}
                        icon={titleIcon}
                        autoHide={autoHideTitle}
                        canCopySubTitle={canCopySubTitle}
                        className={defaultContentTitleClassName}
                    />
                    {
                        !isNullOrEmpty(panel) &&
                        <div className="content-grid-panel">
                            {panel}
                        </div>
                    }
                        <div className={`default-content ${defaultContentClassName} ${autoHideTitle ? addContentSpace ? 'default-content-space' : '' : ''}`}>
                            <div className="default-content-grid">
                                <div className="default-content-grid-children">
                                    {children}
                                </div>
                            </div>
                            
                        </div>
                    </div>
                    
            }
        </PullToRefresh>
    );

    function handleOnOffline() {
        setWarning(navigator.onLine ? "" : "Du er ikke p&aring; nett og lagring skjer kun lokalt");
    }
}

export const ContentGridTitle = ({ title, subTitle, icon, canCopySubTitle, autoHide = false, className = "" }) => {

    return (
        (!isNullOrEmpty(title)) &&
            <div className={`content-grid-title ${autoHide ? 'content-grid-title-hide' : ''} ${className}`} >
                <div className="content-grid-title-text k-d-grid grid-cols-auto-fr k-gap-2">
                    <div style={{ paddingLeft: "min(10px)"} }>
                        {(() => {
                            // todo: swap with IconPicker
                            switch (icon) {
                                case "address-book":
                                    return <AddressBook weight="duotone" style={SvgStyle(getDimensions())} />
                                case "anchor":
                                    return <Anchor weight="duotone" style={SvgStyle(getDimensions())} />
                                case "alarmClock":
                                    return <Alarm weight="duotone" style={SvgStyle(getDimensions())} />
                                case "archive":
                                    return <Archive weight="duotone" style={SvgStyle(getDimensions())} />
                                case "bell-simple-ringing":
                                    return <BellSimpleRinging style={SvgStyle(getDimensions())} weight="duotone" />
                                case "boat":
                                    return <Boat weight="duotone" style={SvgStyle(getDimensions())} />
                                case "book":
                                    return <BookOpenText weight="duotone" style={SvgStyle(getDimensions())} />
                                case "calendar":
                                    return <Calendar weight="duotone" style={SvgStyle(getDimensions())} />
                                case "calendar-star":
                                    return <CalendarStar weight="duotone" style={SvgStyle(getDimensions())} />
                                case "certificate":
                                    return <Certificate weight="duotone" style={SvgStyle(getDimensions())} />
                                case "check":
                                    return <CheckFat weight="duotone" style={SvgStyle(getDimensions())} />
                                case "clock":
                                    return <Clock weight="duotone" style={SvgStyle(getDimensions())} />
                                case "compensationHand":
                                    return <HandCoins weight="duotone" style={SvgStyle(getDimensions())} />
                                case "document":
                                    return <FileText weight="duotone" style={SvgStyle(getDimensions())} />
                                case "evaluation":
                                    return <NotePencil weight="duotone" style={SvgStyle(getDimensions())} />
                                case "handWaving":
                                    return (<HandWaving size={getDimensions()} />)
                                case "hourglass":
                                    return <HourglassMedium weight="duotone" style={SvgStyle(getDimensions())} />
                                case "information":
                                    return <Info weight="duotone" style={SvgStyle(getDimensions())} />
                                case "lifebuoy":
                                    return <Lifebuoy weight="duotone" style={SvgStyle(getDimensions())} />
                                case "lighthouse":
                                    return (<Lighthouse size={getDimensions()} weight="duotone" />)
                                case "medal":
                                    return <Medal weight="duotone" style={SvgStyle(getDimensions())} />
                                case "mobile":
                                    return <DeviceMobile weight="duotone" style={SvgStyle(getDimensions())} />
                                case "next":
                                    return <ArrowFatLinesRight weight="duotone" style={SvgStyle(getDimensions())} />
                                case "paper-plane-tilt":
                                    return <PaperPlaneTilt weight="duotone" style={SvgStyle(getDimensions())} />
                                case "pin":
                                    return <MapPin weight="duotone" style={SvgStyle(getDimensions())} />
                                case "pulse":
                                    return <Pulse weight="duotone" style={SvgStyle(getDimensions())} />
                                case "question-mark":
                                    return <QuestionMark style={SvgStyle(getDimensions())} />
                                case "settings":
                                    return <Gear weight="duotone" style={SvgStyle(getDimensions())} />
                                case "ship":
                                    return (<SvgIcon icon={ShipDuotoneIcon} style={SvgStyle(getDimensions())} />)
                                case "sliders-horizontal":
                                    return <SlidersHorizontal weight="duotone" style={SvgStyle(getDimensions())} />
                                case "stack":
                                    return <Stack weight="duotone" style={SvgStyle(getDimensions())} />
                                case "star":
                                    return <Star weight="duotone" style={SvgStyle(getDimensions())} />
                                case "student":
                                    return <Student weight="duotone" style={SvgStyle(getDimensions())} />
                                case "swap":
                                    return <Swap weight="duotone" style={SvgStyle(getDimensions())} />
                                case "timeReset":
                                    return <ClockCounterClockwise weight="duotone" style={SvgStyle(getDimensions())} />
                                case "toolbox":
                                    return <Toolbox weight="duotone" style={SvgStyle(getDimensions())} />
                                case "userCircle":
                                    return <UserCircle weight="duotone" style={SvgStyle(getDimensions())} />
                                case "graduation-cap":
                                    return <GraduationCap eight="duotone" style={SvgStyle(getDimensions())} />
                                default:
                                    return (<Icons iconName={icon} color={IconColors.Primary} dimensions={getDimensions()} />);
                            }
                        })()}
                    </div>
                    <div>
                        <h1 role="content-title">{parse(title)}</h1>
                        {
                            (!isNullOrEmpty(subTitle)) &&
                            <h2>
                                {
                                    canCopySubTitle ?
                                        <CopyToClipboard title={subTitle} text={subTitle} />
                                        :
                                        parse(subTitle)
                                }
                            </h2>
                        }
                    </div>
                </div>
            </div>
    )

    function getDimensions() {
        if (isNullOrEmpty(subTitle)) return 45;
        return 70;
    }
}
